import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CategoryForm } from "./category-form";

import { FormError, FormModal } from "@/components/kit";

import {
  CATEGORY_FORM_SCHEMA,
  CategoryFormData,
} from "@/schemas/category-form.schema";
import { useCreateCategory } from "@/hooks/api/category/useCreateCategory";

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddCategoryModal({
  isOpen,
  onClose,
  onSuccess,
}: AddCategoryModalProps) {
  const form = useForm({
    resolver: zodResolver(CATEGORY_FORM_SCHEMA),
    mode: "all",
    defaultValues: {
      id: "",
      image: "",
      name: "",
      nameAr: "",
      isHidden: false,
    },
  });

  const name = form.watch("name");

  const {
    mutate: createCategory,
    isPending: isCreatingCategory,
    error,
    isError,
  } = useCreateCategory();

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "An error occured while creating the category, please try again!"
    : null;

  const handleSubmit = (data: CategoryFormData) => {
    createCategory(data, {
      onSuccess: () => {
        form.reset({
          name: "",
          nameAr: "",
          image: "",
          id: "",
          isHidden: false,
        });
        onSuccess();
      },
    });
  };

  const generateId = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  useEffect(() => {
    if (name) {
      form.setValue("id", generateId(name));
    }
  }, [name]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isCreatingCategory}
      submitLabel="Create Category"
      title="Add New Category"
      onClose={onClose}
      onSubmit={() => form.handleSubmit(handleSubmit)()}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <CategoryForm />
      </FormProvider>
    </FormModal>
  );
}
